import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useParameterContext } from "../types/ParameterContext";
import { useClickContext } from "../types/ClickContext";
import { exportTimeSeriesByPolygon, getParameters, getSpatialNames } from '../utils/restapi';
import { getScenrios } from '../utils/restapi';
import { getTimeWindow } from '../utils/restapi';
import { format, set } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./scenariogrid.css"

interface FlyoutProps {
  selectedParameters: string[];
  setSelectedParameters: React.Dispatch<React.SetStateAction<string[]>>
  selectedScenarios: string[];
  setSelectedScenarios: React.Dispatch<React.SetStateAction<string[]>>;

  selectedTime: string | null;
  setSelectedTime: (t: string) => void;
}
const Flyout: React.FC<FlyoutProps> = ({
  selectedParameters,
  setSelectedParameters,
  selectedScenarios,
  setSelectedScenarios,
  selectedTime,
  setSelectedTime
}) => {
  const { parameter, setParameter, scenario, setScenario, time, setTime, calcDiff, setCalcDiff, transparency, setTransparency, plotSelectedTime, spatialAggregationName, setSpatialAggregationName } = useParameterContext()
  const [open, setOpen] = useState(true);
  const { setClickData, clickData } = useClickContext(); // ⬅️ Use context here
  const [isExporting, setIsExporting] = useState(false);
  //list of parameters from the Database
  const [parameters, setParameters] = useState<string[]>([]);
  const [showParameters, setShowParameters] = useState(true);
  const [showCalculations, setShowCalculations] = useState(true);
  const parametersRef = useRef<HTMLDivElement>(null);
  const calculationsRef = useRef<HTMLDivElement>(null);
  const [showScenarios, setShowScenarios] = useState(true);
  const scenariosRef = useRef<HTMLDivElement>(null);

  const [scenarios, setScenarios] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [selectedJsonFile, setSelectedJsonFile] = useState<string>("");
  const [spatialNames, setSpatialNames] = useState<string[]>([]);



  //listen for changes to the selectedtime from the user clicking in the plots.
  useEffect(() => {
    if (plotSelectedTime) {
      const parsed = new Date(plotSelectedTime);
      if (!isNaN(parsed.getTime())) {
        setSelectedDate(parsed);        // ✅ updates DatePicker
        setSelectedTime(plotSelectedTime); // ✅ triggers grid load
        setTime(plotSelectedTime);         // ✅ updates context
      }  
    }
  }, [plotSelectedTime]);

  useEffect(() => {
    if (selectedTime) {
      handlePlotRaster();
    }
  }, [selectedTime]);

  useEffect(() => {
    
    getParameters()
      .then((res) => res.json())
      .then((data) => setParameters(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to fetch parameters:", err));

    getSpatialNames().then((res) => res.json())
      .then((data) => setSpatialNames(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to fetch spatial names:", err));



    // getTimeWindow Endpoint is not implemented correctly yet.
    // getTimeWindow()
    //   .then((res) => res.json())
    //   .then((data) => {
    //     setStartDate(data.startDate)
    //     setEndDate(data.endDate)
    //   })
    //   .catch((err) => console.error("Failed to fetch time window:", err));
  }, []);

  const handleParameterCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedParameters((prev) =>
      prev.includes(value)
        ? prev.filter((parameter: string) => parameter !== value)
        : [...prev, value]


    );
    //set scenario in context
    setParameter((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
    

  }

  const handleScenarioCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedScenarios((prev) =>
      prev.includes(value)
        ? prev.filter((scenario: string) => scenario !== value)
        : [...prev, value]


    );
    //set scenario in context
    setScenario((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const handlePlotRaster = () => {
     if(selectedDate)
     {
       const formattedDate: string = format(selectedDate, 'yyyy-MM-dd');
       setSelectedTime(formattedDate); // This will trigger effect in MapView
       setTime(formattedDate);//set time in context
     }

  };


  const handleNext = () => {
    if (!selectedDate) return;
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    if (nextDay <= (endDate ?? nextDay)) {
      setSelectedDate(nextDay);
      const formattedDate = format(nextDay, 'yyyy-MM-dd');
      setSelectedTime(formattedDate);
      setTime(formattedDate);//set time in context
    }
  };

  
  const handleNextMonth = () => {
    if (!selectedDate) return;
    const nextDay = new Date(selectedDate);
    nextDay.setMonth(nextDay.getMonth() + 1);
    if (nextDay <= (endDate ?? nextDay)) {
      setSelectedDate(nextDay);
      const formattedDate = format(nextDay, 'yyyy-MM-dd');
      setSelectedTime(formattedDate);
      setTime(formattedDate);//set time in context  
    }
  };

  const handleNextYear = () => {
    if (!selectedDate) return;
    const nextYear = new Date(selectedDate);
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    if (nextYear >= (endDate ?? nextYear)) {
      setSelectedDate(nextYear);
      const formattedDate = format(nextYear, 'yyyy-MM-dd');
      setSelectedTime(formattedDate);
      setTime(formattedDate);//set time in context
    }
    
    
  };

  const handlePrev = () => {
    if (!selectedDate) return;
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    if (prevDay >= (startDate ?? prevDay)) {
      setSelectedDate(prevDay);
      const formattedDate = format(prevDay, 'yyyy-MM-dd');
      setSelectedTime(formattedDate);
      setTime(formattedDate);//set time in context
    }
  };

  const handlePrevMonth = () => {
    if (!selectedDate) return;
    const prevMonth = new Date(selectedDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    if (prevMonth >= (endDate ?? prevMonth)) {
      setSelectedDate(prevMonth);
      const formattedDate = format(prevMonth, 'yyyy-MM-dd');
      setSelectedTime(formattedDate);
      setTime(formattedDate);//set time in context
    }
  };

  const handlePrevYear = () => {
    if (!selectedDate) return;
    const prevYear = new Date(selectedDate);
    prevYear.setFullYear(prevYear.getFullYear() - 1);
    if (prevYear >= (endDate ?? prevYear)) {
      setSelectedDate(prevYear);
      const formattedDate = format(prevYear, 'yyyy-MM-dd');
      setSelectedTime(formattedDate);
      setTime(formattedDate);//set time in context
    }
  };
  

  const handleSelectedDateChange = () => {
    if (selectedDate) {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      setSelectedTime(formattedDate);
      setTime(formattedDate);//set time in context
    }
  };

  const handleSelectChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
      const name = e.target.value;
  
      setSelectedJsonFile(name);  //set the selected value for the combobox
      if (setSpatialAggregationName) setSpatialAggregationName(name); //set the selected value in the context for use in the mapview
     
  };
  
    const exportCSV = async () => {
      if (!selectedScenarios || !selectedParameters || clickData === null || !clickData.featureTypeName || !clickData.featureProperties) {
        alert("Please make all selections before exporting.");
        return;
      }
      setIsExporting(true);
      for(let i = 0; i < selectedScenarios.length; i++) {
        for(let j = 0; j < selectedParameters.length; j++) {
          try {
            const response = exportTimeSeriesByPolygon(selectedScenarios[i], selectedParameters[j], clickData.featureTypeName, clickData.featureProperties.OBJECTID);

            if (!(await response).ok) {
              throw new Error("Failed to fetch CSV");
            }

            const blob = await (await response).blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.target = "_blank";
            const name = clickData.featureProperties.NAME || clickData.featureProperties.Name || "unknown";
            link.download = `${clickData.featureTypeName}_${name}_${selectedScenarios[i]}_${selectedParameters[j]}_${getTimestamp()}.csv`;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            alert(`CSV File ${link.download} Downloaded Successfully!`);
            
          } catch (error) {
            console.error("Error downloading CSV:", error);
            alert(`Error exporting CSV: ${String(error)}`);
          } finally {
            setIsExporting(false);
            
          }
        }
      }
    };

    const getTimestamp = () => {
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const yyyy = now.getFullYear();
      const mm = pad(now.getMonth() + 1);
      const dd = pad(now.getDate());
      const hh = pad(now.getHours());
      const min = pad(now.getMinutes());
      return `${yyyy}-${mm}-${dd}-${hh}${min}`;
    };

  return (
    
    <div
      className={`fixed top-0 left-0 h-full bg-white shadow-lg border-r z-[1001] transition-all duration-300 ease-in-out
        ${open ? "w-100" : "w-1"}
      `}
    >
     
      <button
        onClick={() => setOpen(!open)}
        className="absolute top-1/2 right-[-20px] transform -translate-y-1/2 bg-blue-600 text-white w-8 h-8 flex items-center justify-center rounded-r-md shadow-md z-50"
      >
        {open ? <ChevronLeft size={25} /> : <ChevronRight size={25} />}
      </button>

      {open && (
        <div className="p-4 space-y-4"> 
          <div
            ref={scenariosRef}
            className="overflow-y-auto pr-2"
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            <h3
              onClick={() => setShowScenarios(!showScenarios)}
              className="cursor-pointer text-lg font-semibold mb-2 flex justify-between items-center select-none"
            >
              <span>Scenarios</span>
              <span className="text-sm text-blue-600">
                {showScenarios ? "−" : "+"}
              </span>
            </h3>
            {showScenarios && (
              <div className="pl-2">
                <div className="grid-wrapper">
                  {/* <!-- Left Vertical Label --> */}
                  <div className="side-label left-label">Temperature Change (C)</div>

                  {/* <!-- Main Grid --> */}
                  <div className="grid-container">
                    {/* <!-- Title --> */}
                    <div className="title">Mean Precip. Change (%)</div>

                    {/* <!-- Top Column Labels --> */}
                    <div></div>
                    <div className="column-label">-25%</div>
                    <div className="column-label">-12.5%</div>
                    <div className="column-label">0%</div>
                    <div className="column-label">12.5%</div>
                    <div className="column-label">25%</div>
                    <div></div>

                    {/* <!-- Row 1 --> */}
                    <div className="row-label">+5</div>
                    <div className="radio-cell"><input type="checkbox" value="5" checked={selectedScenarios.includes("5")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="10" checked={selectedScenarios.includes("10")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="15" checked={selectedScenarios.includes("15")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="20" checked={selectedScenarios.includes("20")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="24" checked={selectedScenarios.includes("24")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="row-label">+35</div>

                    {/* <!-- Row 2 --> */}
                    <div className="row-label">+4</div>
                    <div className="radio-cell"><input type="checkbox" value="4" checked={selectedScenarios.includes("4")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="9" checked={selectedScenarios.includes("9")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="14" checked={selectedScenarios.includes("14")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="19" checked={selectedScenarios.includes("19")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="23" checked={selectedScenarios.includes("23")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="row-label">+28</div>

                    {/* <!-- Row 3 --> */}
                    <div className="row-label">+3</div>
                    <div className="radio-cell"><input type="checkbox" value="3" checked={selectedScenarios.includes("3")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="8" checked={selectedScenarios.includes("8")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="13" checked={selectedScenarios.includes("13")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="18" checked={selectedScenarios.includes("18")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="22" checked={selectedScenarios.includes("22")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="row-label">+21</div>

                    {/* <!-- Row 4 --> */}
                    <div className="row-label">+2</div>
                    <div className="radio-cell"><input type="checkbox" value="2" checked={selectedScenarios.includes("2")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="7" checked={selectedScenarios.includes("7")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="12" checked={selectedScenarios.includes("12")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="17" checked={selectedScenarios.includes("17")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="radio-cell"><input type="checkbox" value="21" checked={selectedScenarios.includes("21")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div className="row-label">+14</div>

                    {/* <!-- Row 5 (3 radios spaced out) --> */}
                    <div className="row-label">+1</div>
                    <div></div>
                      <div className="radio-cell"><input type="checkbox" value="6" checked={selectedScenarios.includes("6")} onChange={handleScenarioCheckBoxChange}/></div>
                      <div className="radio-cell"><input type="checkbox" value="11" checked={selectedScenarios.includes("11")} onChange={handleScenarioCheckBoxChange}/></div>
                      <div className="radio-cell"><input type="checkbox" value="16" checked={selectedScenarios.includes("16")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div></div>
                    <div className="row-label">+7</div>

                    {/* <!-- Row 6 (1 radio spaced out) --> */}
                    <div className="row-label">0</div>
                    <div></div>
                    <div></div>
                    <div className="radio-cell"><input type="checkbox" value="1" checked={selectedScenarios.includes("1")} onChange={handleScenarioCheckBoxChange}/></div>
                    <div></div>
                    <div></div>	
                    <div className="row-label">0</div>
                  </div>

                  {/* <!-- Right Vertical Label --> */}
                  <div className="side-label right-label">Extreme Precip. Change (%)</div>
                </div>
               
              </div>
            )}
          </div>

          <div
            ref={parametersRef}
            className="overflow-y-auto pr-2"
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            <h3
              onClick={() => setShowParameters(!showParameters)}
              className="cursor-pointer text-lg font-semibold mb-2 flex justify-between items-center select-none"
            >
              <span>Parameters</span>
              <span className="text-sm text-blue-600">
                {showParameters ? "−" : "+"}
              </span>
            </h3>
            {showParameters && (
              <div className="pl-2">
                {parameters.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading parameters...</p>
                ) : (
                  <ul className="text-sm text-gray-800 space-y-1">
                    {parameters.map((param, index) => (
                      <li key={index}>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="parameter"
                            value={param}
                            checked={selectedParameters.includes(param)}
                            onChange={handleParameterCheckBoxChange}
                          />
                          <span>{param}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div ref={calculationsRef}
            className="overflow-y-auto pr-2"
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            <h3
              onClick={() => setShowCalculations(!showCalculations)}
              className="cursor-pointer text-lg font-semibold mb-2 flex justify-between items-center select-none"
            >
              <span>Calculations</span>
              <span className="text-sm text-blue-600">
                {showParameters ? "−" : "+"}
              </span>
            </h3>
            {showCalculations && (
              <div className="pl-2">
                {
                  <ul className="text-sm text-gray-800 space-y-1">
                    {
                      <li key={"Difference"}>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            name="parameter"
                            value={"Difference"}
                             checked={calcDiff}
                             onChange={(e) => setCalcDiff(e.target.checked)}
                          />
                          <span>{"Difference"}</span>
                        </label>
                      </li>
                  }
                  </ul>
                }
                </div>
            )}
          </div>





          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={handlePrevYear}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {/* ←← */}-Y
            </button>
             <button
              onClick={handlePrevMonth}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {/* ←← */}-M
            </button>
            <button
              onClick={handlePrev}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {/* ← */}-D
            </button>

          <DatePicker
            selected={selectedDate}
            onChange={(date: Date | null) => {setSelectedDate(date);handleSelectedDateChange()}}
            minDate={startDate ?? undefined}
            maxDate={endDate ?? undefined}
            placeholderText="Click to select a date"
            dateFormat="MM/dd/yyyy"
            className="border rounded p-2" 
            
          />
            <button
              onClick={handleNext}
              // disabled={selectedTimeIndex === times.length - 1}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              +D
              {/* → */}
            </button>
            <button
              onClick={handleNextMonth}
              // disabled={selectedTimeIndex === times.length - 1}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {/* →→ */}+M
            </button>
            <button
              onClick={handleNextYear}
              // disabled={selectedTimeIndex === times.length - 1}
              className="px-2 py-1 border rounded disabled:opacity-50"
            >
              {/* →→ */}+Y
            </button>

            
          </div>
          

          <div className="mt-4">
            <button
              onClick={handlePlotRaster}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Plot Grid
            </button>
          </div>
            {/* 🔽 Transparency Slider Below This Line */}
          <div className="mt-4">
            <label htmlFor="transparency-slider" className="block text-sm font-medium text-gray-700 mb-1">
              Grid Transparency: {transparency}%
            </label>
            <input
              id="transparency-slider"
              type="range"
              min={0}
              max={100}
              value={transparency}
              onChange={(e) => { if (setTransparency) setTransparency(Number(e.target.value)); }}
              className="w-full"
            />
          </div>
          <div className="p-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Select a Spatial Area:
            </label>
            <select
              value={selectedJsonFile}
              onChange={handleSelectChange}
              className="border border-gray-300 rounded p-2 w-full"
            >
              <option value="">-- Choose an area --</option>
              {spatialNames.length === 0 ?(
                <option value="">-- No areas available --</option>
              ):
                spatialNames.map((name,index) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                )
              )}
            </select>

            {/* {spatialData && (
              <pre className="mt-4 bg-gray-100 p-3 rounded text-sm">
                {JSON.stringify(spatialData, null, 2)}
              </pre>
            )} */}
          </div>
          <div className="mt-4">
            <button
              onClick={exportCSV}
              disabled={isExporting}
              className={`w-full px-4 py-2 rounded ${isExporting ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
            >
                {isExporting ? 'Exporting...' : 'Export CSV'}
            </button>

          </div>
        </div>
      )}
    </div>
  );
};


export default Flyout;